import { MODAL_CSS } from '../styles/modal.css';

export interface ModalOptions {
  iframeUrl: string;
  onClose?: () => void;
  onIframeReady?: () => void;
  onIframeError?: () => void;
}

const IFRAME_LOAD_TIMEOUT_MS = 10_000;

/**
 * Модалка iframe-виджета.
 * Изолирована в Shadow DOM, управляет lifecycle overlay/iframe/skeleton/fallback.
 * Markup строится только через createElement/createElementNS — никакого innerHTML,
 * это и security-хардинг, и совместимость с CSP без 'unsafe-inline' для script-src.
 */
export class Modal {
  private host: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private overlay: HTMLDivElement | null = null;
  private modalEl: HTMLDivElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private fallback: HTMLDivElement | null = null;
  private fallbackLink: HTMLAnchorElement | null = null;
  private skeleton: HTMLDivElement | null = null;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;
  private _isOpen = false;
  private prevBodyOverflow = '';
  // Для корректного сравнения — браузер нормализует setAttribute('src'), и
  // getAttribute('src') может не совпасть с исходной строкой. Храним что применили сами.
  private lastAppliedUrl: string | null = null;

  constructor(private opts: ModalOptions) {}

  get isOpen(): boolean {
    return this._isOpen;
  }

  /** @internal — используется prefill-feature для postMessage в iframe. */
  getIframe(): HTMLIFrameElement | null {
    return this.iframe;
  }

  mount(): void {
    if (this.host) return;

    const host = document.createElement('div');
    host.setAttribute('data-forma-host', '');
    const shadow = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = MODAL_CSS;
    shadow.appendChild(style);

    const overlay = document.createElement('div');
    overlay.className = 'overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Booking widget');
    // Default closed: inert убирает потомков из tab-order и из accessibility tree
    // (SR не анонсит «dialog» пока модалка не открыта). Снимается в open().
    overlay.inert = true;

    const modalEl = document.createElement('div');
    modalEl.className = 'modal';

    // Close-кнопка рендерится внутри iframe (booking-страница) и шлёт FORMA_CLOSE
    // родителю — во внешней модалке кнопки нет. Закрытие overlay-click и Escape остаются.

    const skeleton = document.createElement('div');
    skeleton.className = 'skeleton';

    const iframe = document.createElement('iframe');
    iframe.className = 'iframe';
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-popups allow-same-origin allow-forms allow-storage-access-by-user-activation',
    );
    iframe.setAttribute('title', 'Booking');

    const fallback = document.createElement('div');
    fallback.className = 'fallback';
    const fallbackText = document.createElement('div');
    fallbackText.textContent = 'Не удалось загрузить форму.';
    const fallbackLink = document.createElement('a');
    fallbackLink.setAttribute('href', this.opts.iframeUrl);
    fallbackLink.setAttribute('target', '_blank');
    fallbackLink.setAttribute('rel', 'noopener');
    fallbackLink.textContent = 'Открыть в новой вкладке';
    fallback.appendChild(fallbackText);
    fallback.appendChild(fallbackLink);

    modalEl.appendChild(skeleton);
    modalEl.appendChild(iframe);
    modalEl.appendChild(fallback);
    overlay.appendChild(modalEl);
    shadow.appendChild(overlay);

    document.body.appendChild(host);

    this.host = host;
    this.shadow = shadow;
    this.overlay = overlay;
    this.modalEl = modalEl;
    this.iframe = iframe;
    this.fallback = fallback;
    this.fallbackLink = fallbackLink;
    this.skeleton = skeleton;

    overlay.addEventListener('click', (e) => {
      if (!this._isOpen) return;
      if (e.target === overlay) {
        this.opts.onClose?.();
      }
    });

    iframe.addEventListener('load', () => {
      this.hideSkeleton();
      this.clearLoadTimer();
      // src не задан/пуст → браузер всё равно эмитит load на about:blank; игнорим.
      const src = iframe.getAttribute('src');
      if (src) this.opts.onIframeReady?.();
    });

    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this._isOpen) {
        this.opts.onClose?.();
      }
    };
    document.addEventListener('keydown', this.keydownListener);
  }

  open(): void {
    if (!this.host) this.mount();
    const iframe = this.iframe!;
    const overlay = this.overlay!;
    const skeleton = this.skeleton!;
    const fallback = this.fallback!;

    if (this.lastAppliedUrl !== this.opts.iframeUrl) {
      fallback.classList.remove('visible');
      skeleton.style.display = '';
      this.clearLoadTimer();
      this.loadTimer = setTimeout(() => {
        this.showFallback();
        this.opts.onIframeError?.();
      }, IFRAME_LOAD_TIMEOUT_MS);
      iframe.setAttribute('src', this.opts.iframeUrl);
      this.lastAppliedUrl = this.opts.iframeUrl;
    }

    this.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    overlay.inert = false;

    // rAF чтобы transition начал считать от initial → visible, иначе браузер может схлопнуть шаги.
    requestAnimationFrame(() => {
      overlay.classList.add('visible');
    });

    this._isOpen = true;
  }

  close(): void {
    if (!this._isOpen) return;
    // Парно с CSS `.overlay { pointer-events: none }` (см. modal.css.ts):
    // inert закрывает overlay для SR/focus, pointer-events — для мыши.
    // Без обоих закрытая модалка перехватывает клики/announces «dialog».
    if (this.overlay) {
      this.overlay.classList.remove('visible');
      this.overlay.inert = true;
    }
    document.body.style.overflow = this.prevBodyOverflow;
    this._isOpen = false;
  }

  setUrl(url: string): void {
    this.opts.iframeUrl = url;
    if (this.fallbackLink) {
      this.fallbackLink.setAttribute('href', url);
    }
  }

  destroy(): void {
    this.clearLoadTimer();
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }
    if (this.host) {
      this.host.remove();
    }
    this.host = null;
    this.shadow = null;
    this.overlay = null;
    this.modalEl = null;
    this.iframe = null;
    this.fallback = null;
    this.fallbackLink = null;
    this.skeleton = null;
    this.lastAppliedUrl = null;
    this._isOpen = false;
  }

  private hideSkeleton(): void {
    if (this.skeleton) this.skeleton.style.display = 'none';
  }

  private showFallback(): void {
    this.fallback?.classList.add('visible');
  }

  private clearLoadTimer(): void {
    if (this.loadTimer !== null) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }
}
